import { concat } from '@ember/helper';
import { on } from '@ember/modifier';
import { action } from '@ember/object';
import { type Registry as Services, service } from '@ember/service';
import Component from '@glimmer/component';
import {
  PrismCard,
  PrismIcon,
  PrismIllustration,
} from '@mylighthouse/prism-ember';
import { t } from 'ember-intl';

import styles from './help-center.module.css';

interface HelpCenterSignature {
  Args: {
    accountNumber: string | undefined;
  };
}

export default class HelpCenter extends Component<HelpCenterSignature> {
  @service declare intl: Services['intl'];
  @service declare usageAnalytics: Services['usage-analytics'];

  @action
  openEmailClient(): void {
    this.usageAnalytics.track('Finance Contacted', {
      eventTrigger: 'link',
    });

    const recipients = 'finance@mylighthouse.com';
    const subject = encodeURIComponent(
      this.intl.t('payment.help_center.still_need_help.email_subject', {
        accountNumber: this.args.accountNumber,
      }),
    );

    const mailtoLink = `mailto:${recipients}?subject=${subject}`;

    // Open the email client
    window.open(mailtoLink, '_self');
  }

  <template>
    {{! template-lint-disable link-href-attributes }}
    <div class={{concat styles.container " " "flex gap-400"}}>
      <PrismCard @contentLayout="borderedHeader">
        <:content>
          <div class="flex flex-col items-start gap-300">
            <PrismIllustration @name="40x40_verified" @size="small" />
            <h4>{{t "payments.help_center.tax_certificates.title"}}</h4>
            <p class="paragraph-200--regular">
              {{t "payments.help_center.tax_certificates.description"}}
            </p>
          </div>
        </:content>
        <:footer>
          <a
            class="prism-link prism-link--standalone prism-link--small flex"
            href="https://www.mylighthouse.com/finance/tax-certificates"
            rel="noopener noreferrer"
            target="_blank"
          >{{t "general.learn_more"}}
            <PrismIcon @color="inherit" @name="link-out" @size="small" />
          </a>
        </:footer>
      </PrismCard>
      <PrismCard @contentLayout="borderedHeader">
        <:content>
          <div class="flex flex-col items-start gap-300">
            <PrismIllustration @name="40x40_ebook" @size="small" />
            <h4>{{t "payments.help_center.legal_company_documents.title"}}</h4>
            <p class="paragraph-200--regular">
              {{t "payments.help_center.legal_company_documents.description"}}
            </p>
          </div>
        </:content>
        <:footer>
          <a
            class="prism-link prism-link--standalone prism-link--small flex"
            href="https://www.mylighthouse.com/finance/legal-company-documents"
            rel="noopener noreferrer"
            target="_blank"
          >{{t "general.learn_more"}}
            <PrismIcon @color="inherit" @name="link-out" @size="small" />
          </a>
        </:footer>
      </PrismCard>
      <PrismCard @contentLayout="borderedHeader">
        <:content>
          <div class="flex flex-col items-start gap-300">
            <PrismIllustration @name="40x40_payments" @size="small" />
            <h4>{{t "payments.help_center.vendor_and_bank_details.title"}}</h4>
            <p class="paragraph-200--regular">
              {{t "payments.help_center.vendor_and_bank_details.description"}}
            </p>
          </div>
        </:content>
        <:footer>
          <a
            class="prism-link prism-link--standalone prism-link--small flex"
            href="https://www.mylighthouse.com/finance/vendor-and-bank-details"
            rel="noopener noreferrer"
            target="_blank"
          >{{t "general.learn_more"}}
            <PrismIcon @color="inherit" @name="link-out" @size="small" />
          </a>
        </:footer>
      </PrismCard>
      <PrismCard @contentLayout="borderedHeader">
        <:content>
          <div class="flex flex-col items-start gap-300">
            <PrismIllustration @name="40x40_question" @size="small" />
            <h4>{{t "payments.help_center.still_need_help.title"}}</h4>
            <p class="paragraph-200--regular">
              {{t "payments.help_center.still_need_help.description"}}
            </p>
          </div>
        </:content>
        <:footer>
          <a
            class="prism-link prism-link--standalone prism-link--small flex"
            role="button"
            {{on "click" this.openEmailClient}}
          >{{t "payments.help_center.still_need_help.action"}}
          </a>
        </:footer>
      </PrismCard>
    </div>
  </template>
}
